import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { locale } from 'devextreme/localization';
import { PrimeNG } from 'primeng/config';

import { PRIME_NG_SPANISH } from '../constants/primeng-spanish.locale';
import { localStorageKeys } from './system.service';
import { Language } from '../models/language.model';
import { Parameter } from '../models/parameter.model';

const DEFAULT_LANGUAGE_CODE = 'es';

/** Idioma único (sin catálogo remoto). */
const DEFAULT_LANGUAGES: Language[] = [
  new Language({
    id: 0,
    keyCode: DEFAULT_LANGUAGE_CODE,
    name: 'Español',
    default: true,
    active: true,
  }),
];

/**
 * Parámetros usados por pantallas sin depender del endpoint de settings.
 * Ajusta aquí si necesitas otro comportamiento por defecto.
 */
const DEFAULT_PARAMETERS: Parameter[] = [
  new Parameter({ keyCode: 'ReiniciarPassword', valueData: '1' }),
  new Parameter({ keyCode: 'PasswordUsuarioAutomatico', valueData: '0' }),
];

/**
 * Aplica locale de UI (PrimeNG, DevExtreme) y expone idioma/parámetros en memoria.
 * No realiza peticiones HTTP de configuración inicial.
 */
@Injectable({ providedIn: 'root' })
export class SystemConfigService {
  private initialized = false;
  private initializing: Promise<void> | null = null;

  private readonly languagesSubject = new BehaviorSubject<Language[]>([]);
  private readonly parametersSubject = new BehaviorSubject<Parameter[]>([]);
  private readonly loadedSubject = new BehaviorSubject<boolean>(false);
  private selectedLanguage: Language = new Language({});

  readonly languages$ = this.languagesSubject.asObservable();
  readonly parameters$ = this.parametersSubject.asObservable();
  readonly loaded$ = this.loadedSubject.asObservable();

  constructor(private readonly primeNg: PrimeNG) {}

  get languages(): Language[] {
    return this.languagesSubject.value;
  }

  get parameters(): Parameter[] {
    return this.parametersSubject.value;
  }

  get currentLanguage(): Language {
    return this.selectedLanguage;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initializing) {
      return this.initializing;
    }

    this.initializing = Promise.resolve().then(() => {
      this.languagesSubject.next(DEFAULT_LANGUAGES);
      this.parametersSubject.next(DEFAULT_PARAMETERS);
      this.applyLocaleFromDefaults();
      this.initialized = true;
      this.loadedSubject.next(true);
    });

    try {
      await this.initializing;
    } finally {
      this.initializing = null;
    }
  }

  private applyLocaleFromDefaults(): void {
    const codes = DEFAULT_LANGUAGES.map((l) => l.keyCode).filter((x): x is string => !!x);
    const defaultCode = DEFAULT_LANGUAGE_CODE;
    const stored = localStorage.getItem(localStorageKeys.language);
    const useCode = stored && codes.includes(stored) ? stored : defaultCode;

    if (!stored && useCode) {
      localStorage.setItem(localStorageKeys.language, useCode);
    }

    this.selectedLanguage =
      DEFAULT_LANGUAGES.find((l) => l.keyCode === useCode) ?? DEFAULT_LANGUAGES[0];

    this.primeNg.setTranslation(PRIME_NG_SPANISH);
    try {
      locale(useCode);
    } catch {
      // no-op
    }
  }

  changeLanguage(languageCode: string): void {
    if (!languageCode) return;
    localStorage.setItem(localStorageKeys.language, languageCode);

    this.selectedLanguage =
      DEFAULT_LANGUAGES.find((l) => l.keyCode === languageCode) ?? new Language({});

    this.primeNg.setTranslation(PRIME_NG_SPANISH);
    try {
      locale(languageCode);
    } catch {
      // no-op
    }
  }
}
